import {
  DEFAULT_FONT_FAMILY,
  parseFamilyChain,
  TINYMCE_BASE_FONT_FAMILY,
  unquoteFamily,
} from '@messenger/lotus/fonts'
import {
  EDITOR_FONT_SETTLE_TIMEOUT_MS,
  editorFaceFailed,
  editorFaceInFlight,
  editorFaceLoading,
  retryEditorFaces,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import { EDITOR_TEXT_FACES_FAILED_ATTR } from './textGeometryAttributes'

/** A font-family list's family names, in order, unquoted. */
export { parseFamilyChain as familiesOf } from '@messenger/lotus/fonts'

export type TextBoxFaceSpec = {
  shorthand: string
  text: string
  families: string[]
  weight: number
  italic: boolean
}

export type FaceLike = Pick<
  FontFace,
  'family' | 'weight' | 'style' | 'status' | 'unicodeRange'
>

const FACE_LOAD_SIZE_PX = 16

export const TEXT_BOX_BASE_FAMILY = DEFAULT_FONT_FAMILY
export const TEXT_BOX_BASE_FACE_SHORTHAND = `normal 400 ${FACE_LOAD_SIZE_PX}px ${TINYMCE_BASE_FONT_FAMILY}`
export const TEXT_BOX_BASE_FACE_SAMPLE = 'New Text Box'

export const weightOf = (value: string): number => {
  const word = value.trim().toLowerCase()
  if (word === 'normal') return 400
  if (word === 'bold') return 700
  const n = Number.parseFloat(word)
  return Number.isFinite(n) ? n : 400
}

const weightDistance = (face: FaceLike, weight: number): number => {
  const bounds = face.weight.trim().split(/\s+/).map(weightOf)
  if (bounds.length >= 2) {
    const low = Math.min(bounds[0], bounds[1])
    const high = Math.max(bounds[0], bounds[1])
    if (weight >= low && weight <= high) return 0
    return weight < low ? low - weight : weight - high
  }
  return Math.abs(bounds[0] - weight)
}

const isItalicFace = (face: FaceLike): boolean =>
  face.style.trim().toLowerCase() !== 'normal'

const codePointRanges = (unicodeRange: string): Array<[number, number]> =>
  unicodeRange
    .split(',')
    .map((token) => token.trim().replace(/^u\+/i, ''))
    .filter(Boolean)
    .map((token) => {
      if (token.includes('-')) {
        const [from, to] = token.split('-')
        return [Number.parseInt(from, 16), Number.parseInt(to, 16)]
      }
      if (token.includes('?'))
        return [
          Number.parseInt(token.replace(/\?/g, '0'), 16),
          Number.parseInt(token.replace(/\?/g, 'f'), 16),
        ]
      const single = Number.parseInt(token, 16)
      return [single, single]
    })

export const faceCoversText = (face: FaceLike, text: string): boolean => {
  const ranges = codePointRanges(face.unicodeRange || 'U+0-10FFFF')
  for (const ch of text) {
    const cp = ch.codePointAt(0) ?? 0
    if (ranges.some(([from, to]) => cp >= from && cp <= to)) return true
  }
  return false
}

/**
 * Every face the browser paints a spec's text with: from the first family
 * in the list that any `@font-face` declares, the faces covering the text,
 * the ones in the wanted style when there are any, and of those every face
 * at the nearest weight — a family split into `unicode-range` subsets paints
 * one text with several faces, and each one has to have landed. Empty when
 * the text falls to a system font, which never needs loading.
 */
export const pickTextBoxFaces = (
  faces: readonly FaceLike[],
  spec: Pick<TextBoxFaceSpec, 'families' | 'text' | 'weight' | 'italic'>,
): FaceLike[] => {
  for (const family of spec.families) {
    const declared = faces.filter(
      (face) => unquoteFamily(face.family) === family,
    )
    if (declared.length === 0) continue
    const covering = declared.filter((face) => faceCoversText(face, spec.text))
    if (covering.length === 0) return []
    const styled = covering.filter((face) => isItalicFace(face) === spec.italic)
    const candidates = styled.length > 0 ? styled : covering
    const nearest = Math.min(
      ...candidates.map((face) => weightDistance(face, spec.weight)),
    )
    return candidates.filter(
      (face) => weightDistance(face, spec.weight) === nearest,
    )
  }
  return []
}

/**
 * The one face of those that a single-face reading wants: of equals the one
 * declared last — a later `@font-face` with the same descriptors wins.
 * Null when the text falls to a system font.
 */
export const pickTextBoxFace = (
  faces: readonly FaceLike[],
  spec: Pick<TextBoxFaceSpec, 'families' | 'text' | 'weight' | 'italic'>,
): FaceLike | null => {
  const picked = pickTextBoxFaces(faces, spec)
  return picked.length === 0 ? null : picked[picked.length - 1]
}

/** Whether any family the spec names has a `@font-face` at all. */
export const textBoxFamilyDeclared = (
  faces: readonly FaceLike[],
  spec: Pick<TextBoxFaceSpec, 'families'>,
): boolean =>
  spec.families.some((family) =>
    faces.some((face) => unquoteFamily(face.family) === family),
  )

const textNodesUnder = (root: Node): Text[] => {
  const nodes: Text[] = []
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let node = walker.nextNode()
  while (node) {
    if ((node as Text).data.trim() !== '') nodes.push(node as Text)
    node = walker.nextNode()
  }
  return nodes
}

/**
 * One load spec per face the text under `root` resolves to, with the
 * characters it paints in that face: what `document.fonts.load` needs to
 * fetch exactly the faces the box uses and nothing else.
 */
export const collectTextBoxFaceSpecs = (
  root: HTMLElement,
): TextBoxFaceSpec[] => {
  const specs = new Map<string, TextBoxFaceSpec & { chars: Set<string> }>()
  for (const node of textNodesUnder(root)) {
    const el = node.parentElement
    if (!el) continue
    const cs = getComputedStyle(el)
    if (!cs.fontFamily) continue
    const style = cs.fontStyle || 'normal'
    const weight = cs.fontWeight || '400'
    const shorthand = `${style} ${weight} ${FACE_LOAD_SIZE_PX}px ${cs.fontFamily}`
    let spec = specs.get(shorthand)
    if (!spec) {
      spec = {
        shorthand,
        text: '',
        families: parseFamilyChain(cs.fontFamily),
        weight: weightOf(weight),
        italic: style.toLowerCase() !== 'normal',
        chars: new Set<string>(),
      }
      specs.set(shorthand, spec)
    }
    for (const ch of node.data) if (ch.trim() !== '') spec.chars.add(ch)
  }
  return Array.from(specs.values(), ({ chars, ...spec }) => ({
    ...spec,
    text: Array.from(chars).join(''),
  }))
}

const documentFaces = (): FontFace[] => {
  const faces: FontFace[] = []
  try {
    document.fonts.forEach((face) => faces.push(face))
  } catch {
    return []
  }
  return faces
}

/**
 * Settled means the face's own status says it landed or failed, read with
 * the loads of it the page's retry added (Lotus's face-retry-group): a face
 * that failed while its retry still loads holds the box, and one given up
 * while it still downloads does not. `document.fonts.check` is not asked: it
 * answers true for a face nobody has requested yet, and a box whose face the
 * browser never asked for is exactly the case to catch.
 */
const faceSettled = (face: FaceLike): boolean =>
  face.status !== 'unloaded' && !editorFaceLoading(face)

export const textBoxFaceLoaded = (
  faces: readonly FaceLike[],
  spec: TextBoxFaceSpec,
): boolean => pickTextBoxFaces(faces, spec).every(faceSettled)

export const textBoxFacesPending = (root: HTMLElement): boolean => {
  const faces = documentFaces()
  return collectTextBoxFaceSpecs(root).some(
    (spec) => !textBoxFaceLoaded(faces, spec),
  )
}

/**
 * What a settle concluded about the faces under `root`, for the box to
 * stamp: `loaded` when every face the text resolves to has landed; `error`
 * when one failed and the fallback is there for good; `undeclared` when a
 * family the text names has no `@font-face` at all — a system font, or a
 * stylesheet that never came. `families` names the ones that are not
 * loaded, so a corpus row can be read without guessing.
 */
export type TextBoxFacesOutcome = {
  status: 'loaded' | 'error' | 'undeclared' | 'pending'
  families: string[]
}

export const textBoxFacesOutcome = (root: HTMLElement): TextBoxFacesOutcome => {
  const faces = documentFaces()
  const specs = collectTextBoxFaceSpecs(root)
  const errored = new Set<string>()
  const undeclared = new Set<string>()
  const pending = new Set<string>()
  for (const spec of specs) {
    const picked = pickTextBoxFaces(faces, spec)
    if (picked.length === 0) {
      if (!textBoxFamilyDeclared(faces, spec)) {
        for (const family of spec.families) undeclared.add(family)
      }
      continue
    }
    for (const face of picked) {
      if (editorFaceFailed(face)) errored.add(unquoteFamily(face.family))
      else if (!faceSettled(face)) pending.add(unquoteFamily(face.family))
    }
  }
  if (errored.size > 0) return { status: 'error', families: [...errored] }
  if (pending.size > 0) return { status: 'pending', families: [...pending] }
  if (undeclared.size > 0)
    return { status: 'undeclared', families: [...undeclared] }
  return { status: 'loaded', families: [] }
}

const documentLoaded = (): Promise<void> =>
  new Promise((resolve) => {
    window.addEventListener('load', () => resolve(), { once: true })
  })

/**
 * How long a box waits for its faces on `environment`'s surface: the Lotus
 * limit in the editor and the preview, where a person watches, and none of
 * its own on the pdf, where no one waits, a slow but healthy face must still
 * print, and the worker's page timeout ends the wait.
 */
export const textBoxFaceWaitMs = (
  environment: string | undefined,
): number | null =>
  environment === 'pdf' ? null : EDITOR_FONT_SETTLE_TIMEOUT_MS

/** The faces the text under `root` paints with, in every spec, each once. */
const paintingFacesOf = (root: HTMLElement): FontFace[] => {
  const faces = documentFaces()
  const painting = new Set<FontFace>()
  for (const spec of collectTextBoxFaceSpecs(root))
    for (const face of pickTextBoxFaces(faces, spec))
      painting.add(face as FontFace)
  return [...painting]
}

/**
 * Hands the faces the text under `root` paints with that failed, or still
 * load, to the page's retry (Lotus's `retryEditorFaces`), which loads each
 * once more where the page installed it, the editor and the preview, and
 * tells the page when neither load lands. The pdf installs none.
 */
const retryTextBoxFaces = (root: HTMLElement): void =>
  retryEditorFaces(
    root,
    paintingFacesOf(root).filter(
      (face) => editorFaceFailed(face) || editorFaceLoading(face),
    ),
  )

/**
 * Asks the browser for every face the text uses and resolves once they have
 * landed or failed. A family with no `@font-face` while the document is
 * still loading is not taken for a system font yet: the stylesheet declaring
 * it may still be on its way, so the wait takes the document's load event
 * and looks again. Null when nothing is pending.
 */
const requestTextBoxFaces = (root: HTMLElement): Promise<void> | null => {
  const fonts = document.fonts
  if (!fonts || typeof fonts.load !== 'function') return null
  const faces = documentFaces()
  const specs = collectTextBoxFaceSpecs(root)
  const pending = specs.filter((spec) => !textBoxFaceLoaded(faces, spec))
  const undeclaredWhileLoading =
    document.readyState !== 'complete' &&
    specs.some((spec) => !textBoxFamilyDeclared(faces, spec))
  if (pending.length === 0 && !undeclaredWhileLoading) return null
  const loads = pending.map((spec) =>
    fonts.load(spec.shorthand, spec.text).catch(() => [] as FontFace[]),
  )
  const declaredLater = undeclaredWhileLoading
    ? documentLoaded().then(() => requestTextBoxFaces(root) ?? undefined)
    : null
  return Promise.all([...loads, declaredLater]).then(() => undefined)
}

/**
 * Asks the browser for every face the text under `root` uses and resolves
 * once they have landed or failed, a failed one then showing its fallback
 * for good. A face that fails goes to the page's retry, and the wait goes on
 * while a retry loads: it watches each load's own `loaded` promise, since
 * `document.fonts.load` rejects at once for a face that already failed. With
 * `timeoutMs`, the wait ends at that limit at the latest, and a face still
 * loading then goes to the retry too. Without it, the pdf's, the wait lasts
 * as long as a face does. Null when nothing is pending, so a caller can fit
 * at once.
 */
export const settleTextBoxFaces = (
  root: HTMLElement,
  timeoutMs: number | null = null,
): Promise<void> | null => {
  const requested = requestTextBoxFaces(root)
  if (requested === null) return null
  let over = false
  const settled = (async () => {
    let round: Promise<void> | null = requested
    while (round !== null) {
      await round
      if (over) return
      retryTextBoxFaces(root)
      const loading = paintingFacesOf(root).filter(editorFaceLoading)
      round =
        loading.length === 0
          ? null
          : Promise.all(
              loading.map((face) =>
                face.loaded.then(
                  () => undefined,
                  () => undefined,
                ),
              ),
            ).then(() => undefined)
    }
  })()
  if (timeoutMs === null) return settled
  return new Promise<void>((resolve) => {
    const limit = setTimeout(() => {
      over = true
      retryTextBoxFaces(root)
      resolve()
    }, timeoutMs)
    void settled.then(() => {
      clearTimeout(limit)
      over = true
      resolve()
    })
  })
}

/**
 * Calls `onLanded` when the first of the faces the text under `root` paints
 * with that may still land does: a face still loading, given up or not, or a
 * retry of it. When `onLanded` answers `false`, the box was not ready for the
 * landing, and the next of those faces to land calls it again; otherwise the
 * watch ends. Each face's own `loaded` promise is watched, since the set's
 * `loadingdone` waits until no face on the page loads, and a face stalled
 * elsewhere would hold it. Returns the stop.
 */
export const watchTextBoxLateFaces = (
  root: HTMLElement,
  onLanded: () => boolean | void,
): (() => void) => {
  let stopped = false
  for (const face of paintingFacesOf(root).filter(editorFaceInFlight))
    void face.loaded.then(
      () => {
        if (stopped) return
        if (onLanded() !== false) stopped = true
      },
      () => undefined,
    )
  return () => {
    stopped = true
  }
}

/**
 * On the pdf, names on `container` the families whose face failed for the
 * text under `root` (EDITOR_TEXT_FACES_FAILED_ATTR), as a Lotus box's stamp
 * does, so the worker fails the render instead of printing the fallback, and
 * takes the name back once none reads failed. Off the pdf a failed face
 * settles the box on its fallback, and nothing is named.
 */
export const stampTextBoxFacesFailed = (
  container: Element,
  root: HTMLElement,
  environment: string | undefined,
): void => {
  const outcome = environment === 'pdf' ? textBoxFacesOutcome(root) : null
  if (outcome?.status === 'error')
    container.setAttribute(
      EDITOR_TEXT_FACES_FAILED_ATTR,
      outcome.families.join(', '),
    )
  else container.removeAttribute(EDITOR_TEXT_FACES_FAILED_ATTR)
}

/**
 * A fresh box paints its first paragraph in the editor's base font before a
 * theme font is applied, so the editor asks for that face once at mount: an
 * insert then never fetches it, and never paints the new box in a fallback.
 * The words matter — a face subset by unicode-range loads nothing for the
 * lone space `document.fonts.load` assumes without them.
 */
export const TEXT_BOX_BASE_FACE_WARM_MARK = 'text-box-base-face:warm'
export const TEXT_BOX_BASE_FACE_WARMED_MARK = 'text-box-base-face:warmed'

export const warmTextBoxBaseFace = (): Promise<void> | null => {
  const fonts = document.fonts
  if (!fonts || typeof fonts.load !== 'function') return null
  performance.mark(TEXT_BOX_BASE_FACE_WARM_MARK)
  return fonts
    .load(TEXT_BOX_BASE_FACE_SHORTHAND, TEXT_BOX_BASE_FACE_SAMPLE)
    .then(
      (faces) => {
        performance.mark(TEXT_BOX_BASE_FACE_WARMED_MARK, {
          detail: faces.map(
            (face) =>
              `${face.family} ${face.weight} ${face.style} ${face.status}`,
          ),
        })
      },
      (error) => {
        performance.mark(TEXT_BOX_BASE_FACE_WARMED_MARK, {
          detail: `rejected ${String(error)}`,
        })
      },
    )
}

export const textBoxBaseFaceLoaded = (faces: readonly FaceLike[]): boolean =>
  textBoxFaceLoaded(faces, {
    shorthand: TEXT_BOX_BASE_FACE_SHORTHAND,
    text: TEXT_BOX_BASE_FACE_SAMPLE,
    families: parseFamilyChain(TINYMCE_BASE_FONT_FAMILY),
    weight: 400,
    italic: false,
  }) &&
  pickTextBoxFace(faces, {
    families: parseFamilyChain(TINYMCE_BASE_FONT_FAMILY),
    text: TEXT_BOX_BASE_FACE_SAMPLE,
    weight: 400,
    italic: false,
  }) !== null
